import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  IconButton,
  useMediaQuery,
  ClickAwayListener,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useCompanyFinancialScores } from "../hooks/useCompanyFinancialScores";

const ZONE_CONFIG = {
  distressed: {
    label: "High Risk",
    badge: "High Risk",
    description: "Elevated probability of financial distress. Warrants careful scrutiny of balance sheet health.",
    color: "#ef4444",
    bgColor: "rgba(239, 68, 68, 0.08)",
    borderColor: "rgba(239, 68, 68, 0.2)",
    rangeLabel: "< 1.8",
  },
  grey: {
    label: "Moderate Risk",
    badge: "Grey Zone",
    description: "In the grey zone — financial position is uncertain and warrants ongoing monitoring.",
    color: "#f59e0b",
    bgColor: "rgba(245, 158, 11, 0.08)",
    borderColor: "rgba(245, 158, 11, 0.2)",
    rangeLabel: "1.8 – 2.99",
  },
  safe: {
    label: "Safe Zone",
    badge: "Safe Zone",
    description: "Low probability of financial distress. Company appears financially healthy.",
    color: "#22c55e",
    bgColor: "rgba(34, 197, 94, 0.08)",
    borderColor: "rgba(34, 197, 94, 0.2)",
    rangeLabel: "> 2.99",
  },
} as const;

type Zone = keyof typeof ZONE_CONFIG;

const GOOD_THRESHOLD = 6;

const getZone = (score: number): Zone =>
  score > 2.99 ? "safe" : score >= 1.8 ? "grey" : "distressed";

interface AltmanZScoreChartProps {
  ticker: string;
  height?: number;
  renderCard?: boolean;
}

const AltmanZScoreChart: React.FC<AltmanZScoreChartProps> = ({
  ticker,
  renderCard = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const { data, isLoading, error } = useCompanyFinancialScores(ticker);

  const wrapContent = (content: React.ReactNode) => {
    if (!renderCard) return <>{content}</>;
    return (
      <Card
        elevation={0}
        sx={{
          backgroundColor: isDark ? "#212121" : "#f5f5f5",
          height: "100%",
        }}
      >
        <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>{content}</CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return wrapContent(
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2.5 }}>
          <Skeleton variant="text" width={140} height={20} />
          <Skeleton variant="circular" width={24} height={24} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 1 }}>
          <Skeleton variant="text" width={90} height={52} />
          <Skeleton variant="rectangular" width={90} height={28} sx={{ borderRadius: "2px" }} />
        </Box>
        <Skeleton variant="text" width="80%" height={18} sx={{ mb: 2.5 }} />
        <Skeleton variant="rectangular" height={8} sx={{ borderRadius: "2px", mb: 3 }} />
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={52} sx={{ borderRadius: "2px" }} />
          ))}
        </Box>
      </Box>
    );
  }

  if (!data || data.length === 0 || !!error) {
    return wrapContent(
      <Box sx={{ py: 4, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: isDark ? "#9ca3af" : "#64748b", mb: 1 }}>
          No Altman Z-Score Data
        </Typography>
        <Typography variant="body2" sx={{ color: isDark ? "#6b7280" : "#94a3b8", maxWidth: 340 }}>
          Not available for this company. Common for smaller companies, recent IPOs, or international stocks.
        </Typography>
      </Box>
    );
  }

  const score = data[0].altmanZScore;
  const zone = getZone(score);
  const config = ZONE_CONFIG[zone];
  const minScale = score < 0 ? Math.floor(score / 5) * 5 : 0;
  const maxScale = Math.ceil(Math.max(score, GOOD_THRESHOLD) / 5) * 5;
  const totalRange = maxScale - minScale;
  const pct = (value: number) => ((value - minScale) / totalRange) * 100;
  const scoreMarkerPct = pct(score);
  const thresholdPct = pct(GOOD_THRESHOLD);
  const distressedWidthPct = pct(1.8);
  const greyWidthPct = pct(2.99) - pct(1.8);
  const safeWidthPct = 100 - pct(2.99);

  return wrapContent(
    <>
      {/* Card header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.8125rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            color: isDark ? "#e5e7eb" : "#1d1d1f",
          }}
        >
          Altman Z-Score
        </Typography>
        <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
          <div>
            <Tooltip
              title="The Altman Z-Score predicts bankruptcy probability using 5 financial ratios. Above 2.99 is the safe zone, 1.8–2.99 is the grey zone, and below 1.8 signals elevated distress risk."
              arrow
              open={isMobile ? tooltipOpen : undefined}
              disableFocusListener={isMobile}
              disableHoverListener={isMobile}
              disableTouchListener={isMobile}
              onClose={() => setTooltipOpen(false)}
            >
              <IconButton
                size="small"
                onClick={() => isMobile && setTooltipOpen(!tooltipOpen)}
                sx={{ color: isDark ? "#6b7280" : "#9ca3af", p: 0.5 }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </div>
        </ClickAwayListener>
      </Box>

      {/* Score hero + zone badge */}
      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", mb: 1 }}>
        <Typography sx={{ fontSize: "2.75rem", fontWeight: 700, lineHeight: 1, color: config.color }}>
          {score.toFixed(2)}
        </Typography>
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            backgroundColor: config.bgColor,
            border: `1px solid ${config.borderColor}`,
            borderRadius: "2px",
            mb: 0.5,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: config.color, fontSize: "0.75rem" }}>
            {config.badge}
          </Typography>
        </Box>
      </Box>

      {/* Context description */}
      <Typography sx={{ color: isDark ? "#9ca3af" : "#6b7280", fontSize: "0.8125rem", lineHeight: 1.5 }}>
        {config.description}
      </Typography>

      {/* Progress bar */}
      <Box sx={{ mt: 2.5, mb: 0.5 }}>
        {/* Score marker above bar */}
        <Box sx={{ position: "relative", height: 24, mb: 0.5 }}>
          <Box
            sx={{
              position: "absolute",
              left: `clamp(4%, ${scoreMarkerPct}%, 96%)`,
              transform: "translateX(-50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontWeight: 700, color: config.color, fontSize: "0.6875rem", lineHeight: 1 }}>
              {score.toFixed(2)}
            </Typography>
            <Box
              sx={{
                width: 0,
                height: 0,
                borderLeft: "4px solid transparent",
                borderRight: "4px solid transparent",
                borderTop: `5px solid ${config.color}`,
                mt: "3px",
              }}
            />
          </Box>
        </Box>

        {/* Three-zone bar with threshold marker */}
        <Box sx={{ position: "relative" }}>
          <Box sx={{ display: "flex", height: 8, borderRadius: "2px", overflow: "hidden" }}>
            <Box sx={{ width: `${distressedWidthPct}%`, backgroundColor: "#ef4444", flexShrink: 0 }} />
            <Box sx={{ width: `${greyWidthPct}%`, backgroundColor: "#f59e0b", flexShrink: 0 }} />
            <Box sx={{ width: `${safeWidthPct}%`, backgroundColor: "#22c55e", flexShrink: 0 }} />
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: -4,
              left: `${thresholdPct}%`,
              transform: "translateX(-50%)",
              width: 2,
              height: 16,
              backgroundColor: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.4)",
              borderRadius: "1px",
            }}
          />
        </Box>

        {/* Scale end labels + threshold label */}
        <Box sx={{ position: "relative", mt: 0.75 }}>
          <Typography sx={{ fontSize: "0.625rem", color: isDark ? "#6b7280" : "#9ca3af" }}>{minScale}</Typography>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: `${thresholdPct}%`,
              transform: "translateX(-50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: "0.5625rem", color: isDark ? "#9ca3af" : "#6b7280", whiteSpace: "nowrap" }}>
              6+ strong
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Zone reference grid */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5, mt: 2 }}>
        {(["distressed", "grey", "safe"] as Zone[]).map((z) => {
          const isActive = zone === z;
          const c = ZONE_CONFIG[z];
          return (
            <Box
              key={z}
              sx={{
                p: 1.5,
                backgroundColor: isActive ? c.bgColor : "transparent",
                border: `1px solid ${isActive ? c.borderColor : theme.palette.divider}`,
                borderRadius: "2px",
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.6875rem",
                  color: isActive ? c.color : (isDark ? "#6b7280" : "#9ca3af"),
                  display: "block",
                  mb: 0.25,
                }}
              >
                {c.label}
              </Typography>
              <Typography sx={{ fontSize: "0.625rem", color: isDark ? "#4b5563" : "#cbd5e1" }}>
                {c.rangeLabel}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </>
  );
};

export default AltmanZScoreChart;
